// File: src/pages/neutral/NeutralPortal.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useContext,
  useCallback,
} from 'react';

import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';

import NeutralPortalNavBar from '../../components/neutral/NeutralPortalNavBar';
import NeutralChatInput from '../../components/neutral/NeutralChatInput';
import { getNeutralSocket } from '../../utils/neutralSocket';
import { LuClipboardList, LuMapPin, LuUserCheck } from 'react-icons/lu';
import WhereToMeetSheet from '../../components/WhereToMeetSheet';

function authHeaders() {
  const t = localStorage.getItem('accessToken');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

function getUid() {
  try {
    const auth = JSON.parse(localStorage.getItem('tribe_auth') || '{}');
    return (
      auth?.uid ||
      auth?.userId ||
      auth?.user?._id ||
      localStorage.getItem('userId') ||
      ''
    );
  } catch {
    return localStorage.getItem('userId') || '';
  }
}

function EmptyChatState() {
  return (
    <div
      style={{
        minHeight: 360,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '38px 18px',
        textAlign: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 390 }}>
        <div
          style={{
            position: 'relative',
            width: 250,
            height: 150,
            margin: '0 auto 26px',
          }}
          aria-hidden="true"
        >
          {/* left bubble */}
          <div
            style={{
              position: 'absolute',
              left: 15,
              top: 24,
              width: 94,
              height: 94,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 16px 22px rgba(15,23,42,0.16)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 1,
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -7,
                left: '50%',
                width: 22,
                height: 22,
                background: '#fff',
                transform: 'translateX(-50%) rotate(45deg)',
                borderRadius: 4,
                zIndex: -1,
              }}
            />

            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#FFF4B8',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 44,
                  lineHeight: 1,
                  transform: 'translateY(-1px)',
                  filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.12))',
                }}
              >
                📍
              </span>
            </div>
          </div>

          {/* right bubble */}
          <div
            style={{
              position: 'absolute',
              right: 15,
              top: 24,
              width: 94,
              height: 94,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 16px 22px rgba(15,23,42,0.16)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 1,
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -7,
                left: '50%',
                width: 22,
                height: 22,
                background: '#fff',
                transform: 'translateX(-50%) rotate(45deg)',
                borderRadius: 4,
                zIndex: -1,
              }}
            />

            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#DDEBFF',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 43,
                  lineHeight: 1,
                  transform: 'translateY(-1px)',
                  filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.12))',
                }}
              >
                🗼
              </span>
            </div>
          </div>

          {/* center bubble */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 5,
              width: 116,
              height: 116,
              borderRadius: '50%',
              background: '#fff',
              transform: 'translateX(-50%)',
              boxShadow: '0 18px 28px rgba(15,23,42,0.18)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 2,
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -8,
                left: '50%',
                width: 26,
                height: 26,
                background: '#fff',
                transform: 'translateX(-50%) rotate(45deg)',
                borderRadius: 5,
                zIndex: -1,
              }}
            />

            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: '#FFE1E6',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 57,
                  lineHeight: 1,
                  transform: 'translateY(-1px)',
                  filter: 'drop-shadow(0 4px 5px rgba(0,0,0,0.13))',
                }}
              >
                👕
              </span>
            </div>
          </div>
        </div>

        <h2
          style={{
            margin: 0,
            fontSize: 'clamp(.9rem, 6vw, 3rem)',
            lineHeight: 1.05,
            fontWeight: 750,
            letterSpacing: '-0.065em',
            color: '#050505',
          }}
        >
          Start the group chat
        </h2>

        <p
          style={{
            maxWidth: 350,
            margin: '13px auto 0',
            fontSize: 'clamp(.8rem, 2.8vw, 1.22rem)',
            lineHeight: 1.38,
            fontWeight: 500,
            color: '#111',
          }}
        >
          Share where you are, what you’re wearing, or a nearby landmark to help
          everyone find the group.
        </p>
      </div>
    </div>
  );
}

export default function NeutralPortal() {
  const { eventId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const event = state?.event || null;
  const exitingRef = useRef(false);

  const api = process.env.REACT_APP_API_URL;

  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [people, setPeople] = useState({});
  const [loading, setLoading] = useState(true);


  // ✅ Where to meet sheet
  const [whereToMeetOpen, setWhereToMeetOpen] = useState(false);

function openAgenda() {
  if (!isHost) return;

  const targetEventId = String(
    eventId || ''
  ).replace(/^neutral-/, '');

  navigate(
    `/neutral/${targetEventId}/agenda`,
    {
      state: { event },
    }
  );
}


  function openGuestCheckIn() {
    if (!isHost) return;

    const targetEventId = String(eventId || '').replace(/^neutral-/, '');

    navigate(`/neutral/${targetEventId}/check-in`, {
      state: { event },
    });
  }

  const me = useMemo(() => String(getUid() || ''), []);
  const socket = useMemo(() => getNeutralSocket(), []);

  const { currentUser } = useContext(AuthContext);

  const [meProfile, setMeProfile] = useState(null);

  function normalizeEmail(s) {
    return String(s || '').trim().toLowerCase();
  }

  const myEmail = useMemo(() => {
    return normalizeEmail(currentUser?.email || meProfile?.email || '');
  }, [currentUser, meProfile]);

  const promoterId = useMemo(() => {
    return String(
      state?.event?.promoterId?._id ||
        state?.event?.promoterId ||
        event?.promoterId?._id ||
        event?.promoterId ||
        ''
    );
  }, [state, event]);

  const assignedPromoterEmail = useMemo(() => {
    return normalizeEmail(
      state?.event?.promoterId?.email ||
        event?.promoterId?.email ||
        ''
    );
  }, [state, event]);

  const isHost = useMemo(() => {
    if (promoterId && String(me) === promoterId) return true;

    if (
      currentUser?.isHost &&
      assignedPromoterEmail &&
      myEmail &&
      assignedPromoterEmail === myEmail
    ) {
      return true;
    }

    return false;
  }, [me, promoterId, currentUser, assignedPromoterEmail, myEmail]);

  const isHostSender = useCallback(
    (senderId, senderEmail = '') => {
      const cleanSenderEmail = normalizeEmail(senderEmail);

      if (
        assignedPromoterEmail &&
        cleanSenderEmail &&
        cleanSenderEmail === assignedPromoterEmail
      ) {
        return true;
      }

      if (promoterId && String(senderId) === promoterId) return true;

      return false;
    },
    [promoterId, assignedPromoterEmail]
  );

  const beginExit = () => {
    exitingRef.current = true;
  };

  const handleExit = () => {
    beginExit();

    try {
      const sock = getNeutralSocket();
      sock?.off();
      sock?.disconnect();
    } catch {}

    const targetEventId = String(eventId).replace(/^neutral-/, '');

    requestAnimationFrame(() => {
      navigate(`/events/${targetEventId}`, {
        state: {
          fromNeutralPortal: true,
          event,
        },
        replace: true,
      });
    });
  };

  const handleDirections = () => {
    if (!event?.latitude || !event?.longitude) {
      alert('No location available for this gathering.');
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`,
      '_blank'
    );
  };

  useEffect(() => {
    if (!me) return;

    let alive = true;

    (async () => {
      try {
        const { data } = await axios.get(`${api}/users/${me}`, authHeaders());
        if (alive) setMeProfile(data || null);
      } catch {
        // noop
      }
    })();

    return () => {
      alive = false;
    };
  }, [api, me]);

  // Scrollable GREY container + invisible tail anchor
  const listRef = useRef(null);
  const tailRef = useRef(null);

  const pinBottomSoft = (smooth = false) => {
    if (tailRef.current) {
      tailRef.current.scrollIntoView({
        block: 'end',
        behavior: smooth ? 'smooth' : 'auto',
      });
      return;
    }

    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const pinBottomHard = () => {
    const el = listRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };

  function makeTempMessage(text) {
    return {
      messageId: `tmp_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      roomId,
      senderId: me,
      content: text,
      createdAt: new Date().toISOString(),
      optimistic: true,
    };
  }

  // socket: enter + get room id (guard duplicates)
  useEffect(() => {
    if (!socket || !eventId) return;

    try {
      socket.emit('neutralPortal:enter', { eventId });
    } catch {}

    const onDenied = (p) => console.warn('[neutral:accessDenied]', p);

    const onReady = ({ roomId: rid }) => {
      setRoomId((prev) => (prev === rid ? prev : rid));

      try {
        socket.emit('join', { roomId: rid });
      } catch {}
    };

    socket.on('neutral:accessDenied', onDenied);
    socket.on('neutral:room-ready', onReady);

    return () => {
      socket.off('neutral:accessDenied', onDenied);
      socket.off('neutral:room-ready', onReady);
    };
  }, [socket, eventId]);

  // initial history + live stream
  useEffect(() => {
    if (!roomId) return;

    let alive = true;

    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/chat/rooms/${roomId}/messages`,
          authHeaders()
        );

        if (!alive) return;
        setMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn(
          '[NeutralPortal] history failed:',
          e?.response?.data || e?.message
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    const onNew = (payload) => {
      if (!payload || String(payload.roomId) !== String(roomId)) return;

      setMessages((prev) => {
        const incomingText = payload.content ?? payload.text ?? '';
        const idx = prev.findIndex(
          (m) =>
            m.optimistic === true &&
            String(m.senderId) === me &&
            (m.content ?? m.text ?? '') === incomingText
        );

        if (idx !== -1) {
          const next = prev.slice();
          next[idx] = { ...payload };
          return next;
        }

        return prev.concat([payload]);
      });

      requestAnimationFrame(() => pinBottomSoft(true));
    };

    socket.on('newMessage', onNew);

    return () => {
      alive = false;
      socket.off('newMessage', onNew);
    };
  }, [api, roomId, socket, me]);

  // lazy-load sender profiles
  useEffect(() => {
    const ids = new Set(
      messages
        .map((m) => String(m.senderId || m.sender?._id || m.sender?.id || ''))
        .filter(Boolean)
    );

    const missing = Array.from(ids).filter((id) => {
      const existing = people[id];

      if (!existing) return true;

      // Host tag can depend on email, so fetch once if email is missing.
      if (!existing.email && existing._profileLookupDone !== true) return true;

      return false;
    });

    if (!missing.length) return;

    let cancelled = false;

    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const { data } = await axios.get(
                `${api}/users/${id}`,
                authHeaders()
              );

              return {
                id,
                firstName: data?.firstName || '',
                lastName: data?.lastName || '',
                email: data?.email || '',
                profilePicUrl: data?.profilePicUrl || '/default-avatar.png',
                _profileLookupDone: true,
              };
            } catch {
              return {
                id,
                firstName: '',
                lastName: '',
                email: '',
                profilePicUrl: '/default-avatar.png',
                _profileLookupDone: true,
              };
            }
          })
        );

        if (cancelled) return;

        setPeople((prev) => {
          const next = { ...prev };

          for (const p of results) {
            next[p.id] = {
              ...(next[p.id] || {}),
              ...p,
            };
          }

          return next;
        });
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, people, api]);

  // Keep pinned to bottom on load and on message count changes
  useLayoutEffect(() => {
    if (!loading) {
      pinBottomHard();
      setTimeout(pinBottomHard, 0);
    }
  }, [loading, messages.length]);

  // iOS keyboard / viewport shim: keep bottom dock visible and list scrolled
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      pinBottomSoft(true);
    };

    vv.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);

    return () => {
      vv.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  async function send(text) {
    if (!roomId) return;

    const tmp = makeTempMessage(text);
    setMessages((prev) => prev.concat([tmp]));
    requestAnimationFrame(() => pinBottomSoft(true));

    try {
      await axios.post(
        `${api}/chat/rooms/${roomId}/messages`,
        { content: text },
        authHeaders()
      );
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.messageId === tmp.messageId
            ? { ...m, optimistic: false, error: true }
            : m
        )
      );

      console.warn(
        '[NeutralPortal] send failed:',
        e?.response?.data || e?.message
      );
    }
  }

  function displayNameFor(id) {
    const p = people[id];

    if (!p) return `User ${String(id).slice(-6)}`;

    const fn = (p.firstName || '').trim();
    const ln = (p.lastName || '').trim();

    return fn || ln
      ? `${fn}${ln ? ' ' + ln.charAt(0) + '.' : ''}`
      : `User ${String(id).slice(-6)}`;
  }

  function avatarFor(id) {
    const p = people[id];
    return (p && p.profilePicUrl) || '/default-avatar.png';
  }

  function timeFor(ts) {
    try {
      return ts
        ? new Date(ts).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })
        : '';
    } catch {
      return '';
    }
  }

  const headerFirst = (meProfile?.firstName || '').trim();
  const headerTopText = `WELCOME ${headerFirst ? headerFirst.toUpperCase() : '…'} TO`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        height: '100dvh',
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      {/* Row 1: Navbar + centered welcome header */}
      <div style={{ overflow: 'hidden' }}>
        <NeutralPortalNavBar
          eventId={eventId}
          helpTitle="Group Chat"
          helpBody="Use this channel to coordinate meet points, directions, and timing."
          onBack={handleExit}
        />

        <div
          style={{
            maxWidth: 720,
            width: '94%',
            margin: '8px auto',
            padding: '0 12px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 'clamp(1rem, 2.6vw, 1.05rem)',
              fontWeight: 900,
              letterSpacing: '0.06em',
              color: '#000',
              textTransform: 'uppercase',
              marginTop: 0,
            }}
          >
            {headerTopText}
          </div>

          <div
            style={{
              fontSize: 'clamp(1.35rem, 4.2vw, 2.2rem)',
              lineHeight: 1.25,
              fontWeight: 900,
              letterSpacing: '-0.01em',
              color: '#0B1020',
              marginTop: 8,
            }}
          >
            {event?.name || 'Event'}
          </div>

          <p style={{ color: '#6b7280', margin: '12px 0 12px', fontSize: 14 }}>
            Use the chat if you need help finding the group.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'nowrap',
              marginBottom: 10,
              width: '100%',
            }}
          >
            <button
              type="button"
              onClick={() => setWhereToMeetOpen(true)}
              style={{
                height: 34,
                padding: '0 9px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: '#fff',
                color: '#0b0b0b',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              <LuMapPin size={14} strokeWidth={2.35} />
              Where to meet
            </button>

            {isHost ? (
              <>
                <button
                  type="button"
                  onClick={openGuestCheckIn}
                  style={{
                    height: 34,
                    padding: '0 9px',
                    borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    color: '#0b0b0b',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title="Check in guests"
                >
                  <LuUserCheck size={14} />
                  Check in guests
                </button>

                <button
                  type="button"
                  onClick={openAgenda}
                  style={{
                    height: 34,
                    padding: '0 9px',
                    borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    color: '#0b0b0b',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title="View agenda"
                >
                  <LuClipboardList size={14} />
                  View agenda
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Row 2: GREY MESSAGE BOX */}
      <div
        ref={listRef}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          borderTop: '1px solid #eef0f2',
          borderBottom: '1px solid #eef0f2',
          background: '#fafafa',
        }}
      >
        <div
          style={{
            maxWidth: 720,
            width: '94%',
            margin: '10px auto',
            padding: '0 12px',
          }}
        >
          {loading && <div style={{ padding: 8, color: '#64748b' }}>Loading…</div>}

          {!loading && !messages.length && <EmptyChatState />}

          {messages.map((m) => {
            const sid = String(m.senderId || '');
            const mine = sid === me;
            const sender = m.sender || null;

            const name = sender
              ? `${sender.firstName}${
                  sender.lastName ? ' ' + sender.lastName.charAt(0) + '.' : ''
                }`
              : displayNameFor(sid);

            const avatar = sender?.profilePicUrl || avatarFor(sid);
            const when = timeFor(m.createdAt);

            const senderIsHost = isHostSender(
              sid,
              sender?.email || people[sid]?.email || ''
            );

            return (
              <div
                key={m.messageId || m._id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  margin: '10px 0',
                  flexDirection: mine ? 'row-reverse' : 'row',
                }}
              >
                <div
                  style={{
                    width: 44,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={avatar}
                    alt=""
                    onError={(e) => (e.currentTarget.src = '/default-avatar.png')}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid #e5e7eb',
                      display: 'block',
                    }}
                  />

                  {senderIsHost && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        padding: '6px 6px',
                        borderRadius: 9999,
                        background: '#e6e4e4ff',
                        color: '#555',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                      title="Host"
                    >
                      Host
                    </div>
                  )}
                </div>

                <div style={{ maxWidth: '78%', textAlign: mine ? 'right' : 'left' }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    {name}
                    {when ? ` • ${when}` : ''}
                  </div>

                  <div
                    style={{
                      display: 'inline-block',
                      textAlign: 'left',
                      whiteSpace: 'pre-wrap',
                      background: mine ? '#007BFF' : '#E0E0E0',
                      color: mine ? '#fff' : '#111',
                      borderRadius: 12,
                      padding: '10px 12px',
                      lineHeight: '1.45',
                      wordBreak: 'break-word',
                      opacity: m.optimistic ? 0.6 : 1,
                    }}
                  >
                    {m.text || m.content}
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={tailRef} />
        </div>
      </div>

      {/* Row 3: FIXED DOCK */}
      <div
        onFocusCapture={() => pinBottomSoft(true)}
        onClickCapture={() => pinBottomSoft(true)}
        style={{ background: '#fff', zIndex: 10 }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <NeutralChatInput
            onSend={send}
            placeholder="Share a meet point, landmark, or timing…"
          />
        </div>
      </div>

      <WhereToMeetSheet
        open={whereToMeetOpen}
        onClose={() => setWhereToMeetOpen(false)}
        heading="Where to meet"
        subheading="Use this spot to find your group when you arrive."
        spot={event?.whereToMeetSpot || 'Near the front entrance by the patio.'}
        note={
          event?.whereToMeetNote ||
          'Look for your host or check the group chat for updates when they arrive.'
        }
        imageUrl={event?.whereToMeetImageUrl}
        chatNote="Use this chat portal if you need help finding the group."
        onDirections={handleDirections}
      />
    </div>
  );
}